/**
 * optimizeImage — convert image URLs to optimized variants at runtime.
 *
 * Unsplash supports on-the-fly format conversion via `fm` and `q` params.
 * Squarespace's CDN serves the original (often 3000+ px / 500 KB+) by default
 * but accepts `?format=NNNw` (e.g. `format=800w`) to resize down — huge win
 * for our portfolio/gallery photos.
 * Pexels supports `auto=compress`.
 * For other URLs we return as-is.
 */
export function optimizeImage(url, { width, quality = 75 } = {}) {
  if (!url) return url;

  const isUnsplash = url.includes("images.unsplash.com");
  const isPexels = url.includes("images.pexels.com");
  const isSquarespace = url.includes("images.squarespace-cdn.com");

  if (!isUnsplash && !isPexels && !isSquarespace) return url;

  // Squarespace uses path-style `?format=Nw` (no other dimensions). Use the
  // closest standard size to the rendered width.
  if (isSquarespace) {
    const [base] = url.split("?");
    const target = width && width <= 500 ? 500
      : width && width <= 750 ? 750
      : width && width <= 1000 ? 1000
      : width && width <= 1500 ? 1500
      : 2500;
    return `${base}?format=${target}w`;
  }

  // Unsplash / Pexels
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  if (isUnsplash) {
    params.set("fm", "webp");
    params.set("q", String(quality));
    if (width && !params.has("w")) params.set("w", String(width));
  }
  if (isPexels) {
    if (width && !params.has("w")) params.set("w", String(width));
    if (!params.has("auto")) params.set("auto", "compress");
  }
  return `${base}?${params.toString()}`;
}

/**
 * buildSrcSet — generate a comma-separated srcset for responsive images.
 * Widths in pixels. Use with sizes="100vw" for hero images, or
 * sizes="(min-width: 768px) 50vw, 100vw" for two-column grids.
 */
export function buildSrcSet(url, widths = [640, 1024, 1536, 1920]) {
  return widths
    .map((w) => `${optimizeImage(url, { width: w, quality: w > 1200 ? 78 : 72 })} ${w}w`)
    .join(", ");
}
